import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { type Page, type PageQuery } from "_/utils";

type CursorPaginationOptions<T> = {
  queryKey: readonly unknown[];
  getPage: (query: PageQuery) => Promise<Page<T>>;
  pageSize?: number;
};

/**
 * Fetches cursor-based API pages and keeps enough history to navigate back to
 * newer results. Page endpoints use the same `before`/`after` cursor shape,
 * so table listings can share this behavior.
 */
export function useCursorPagination<T>({
  queryKey,
  getPage,
  pageSize = 50,
}: CursorPaginationOptions<T>) {
  const [pageQueries, setPageQueries] = useState<PageQuery[]>([
    { limit: pageSize },
  ]);
  const pageQuery = pageQueries.at(-1)!;
  const query = useQuery({
    queryKey: [...queryKey, pageQuery],
    queryFn: () => getPage(pageQuery),
    placeholderData: keepPreviousData,
  });
  const olderPageQuery = query.data?.before
    ? { limit: pageSize, before: query.data.before }
    : undefined;
  // Prefetch the next page so we only enable Next when it contains results.
  const olderPage = useQuery({
    queryKey: [...queryKey, olderPageQuery],
    queryFn: () => {
      if (!olderPageQuery) {
        throw new Error("Cannot fetch an older page without a cursor.");
      }

      return getPage(olderPageQuery);
    },
    enabled: !query.isPlaceholderData && olderPageQuery !== undefined,
  });

  const showOlder = () => {
    if (
      query.isPlaceholderData ||
      !olderPageQuery ||
      !olderPage.data?.items.length
    ) {
      return;
    }

    setPageQueries((queries) => [...queries, olderPageQuery]);
  };

  const showNewer = () => {
    if (query.isPlaceholderData) return;

    setPageQueries((queries) => queries.slice(0, -1));
  };

  const reset = useCallback(() => {
    setPageQueries([{ limit: pageSize }]);
  }, [pageSize]);

  return {
    ...query,
    showOlder,
    showNewer,
    reset,
    currentPage: pageQueries.length,
    canShowOlder: Boolean(olderPage.data?.items.length),
    canShowNewer: pageQueries.length > 1,
  };
}
